import { ApolloError, gql, useMutation } from '@apollo/client';
import { useCallback, useRef, useState } from 'react';
import { uploadToFilestack } from 'src/lib/filestack';
import { notify } from 'src/lib/notify';

export type FeedbackType = 'IDEA' | 'PROBLEM' | 'QUESTION';

const SUBMIT_CUSTOMER_FEEDBACK = gql`
  mutation SubmitCustomerFeedback($input: SubmitCustomerFeedbackInput!) {
    submitCustomerFeedback(input: $input) {
      identifier
      issueUrl
    }
  }
`;

type Submission = { identifier: string; issueUrl: string };
type FeedbackDraft = { category: FeedbackType; body: string; pageUrl: string; recordRef?: string };

const RETRY_MESSAGE = "Couldn't send, try again.";

/** A permanent failure (e.g. invalid input) says why; anything retryable asks the user to resend. */
export function feedbackSendFailureMessage(error: unknown): string {
  const graphQLError = error instanceof ApolloError ? error.graphQLErrors[0] : undefined;
  return graphQLError?.extensions?.retryable === false && graphQLError.message ? graphQLError.message : RETRY_MESSAGE;
}

export function useSubmitCustomerFeedback() {
  const [submitMutation, state] = useMutation<{ submitCustomerFeedback: Submission }>(SUBMIT_CUSTOMER_FEEDBACK, {
    // The drawer owns the error toast; keep the shared error link from showing a second one.
    context: { appErrorPresentation: 'inline' },
  });
  const [submitting, setSubmitting] = useState(false);
  const submissionRef = useRef<Promise<Submission | undefined> | null>(null);
  // One id per draft: a resend after a timeout finds the issue the first send already filed.
  const draftIdRef = useRef<string | null>(null);

  const submit = useCallback(
    (input: FeedbackDraft, screenshot?: Blob) => {
      if (submissionRef.current) return submissionRef.current;

      setSubmitting(true);
      draftIdRef.current ??= crypto.randomUUID();
      const clientSubmissionId = draftIdRef.current;
      const submission = (async () => {
        const screenshotUrl = screenshot
          ? (await uploadToFilestack(screenshot, `feedback-${clientSubmissionId}.jpg`)).url
          : undefined;
        const result = await submitMutation({ variables: { input: { ...input, clientSubmissionId, screenshotUrl } } });
        const filed = result.data?.submitCustomerFeedback;
        if (filed) {
          draftIdRef.current = null;
          notify.success(`Thanks, tracked as ${filed.identifier}`);
        }
        return filed;
      })().finally(() => {
        submissionRef.current = null;
        setSubmitting(false);
      });
      submissionRef.current = submission;
      return submission;
    },
    [submitMutation]
  );

  return { submit, loading: submitting || state.loading, error: state.error };
}
